import { useState, useEffect, useRef } from "react";
import { Button, DatePicker, Input, Space } from "antd";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { getBuyersTransferedToBuyAndSupplyWalletApi } from "Network";
import { toastError } from "helpers/toasters";
import {
  exportDataAsExcel,
  handleAllowNumbersOnly,
} from "helpers/helpfulFunctions";

import SubHeader from "../SubComponents/SubHeader";
import GridTable from "modules/SubComponents/GridTable/GridTable";
import Header from "modules/SubComponents/Header";
import { useAuth } from "hooks/useAuth";

function ExtendToBuyAndSupplyWallet() {
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [searchValues, setSearchValues] = useState({
    userName: "",
    userMobile: "",
    type: "",
    quantity: "",
    price: "",
    commission: "",
    totalPriceAfterCommission: "",
    totalPriceBeforeCommission: "",
    cashFromUser: "",
    cashToUser: "",
    moneyCollectionType: "",
    from: "",
    to: "",
    sellingDateFrom: "",
    sellingDateTo: "",
    offeredBy: "",
    salesPerson: "",
  });

  const [sellingDateRange, setSellingDateRange] = useState([]);
  const [creationDateRange, setCreationDateRange] = useState([]);
  const { RangePicker } = DatePicker;
  const searchInput = useRef(null);

  const { adminRoles } = useAuth();
  const isSales = adminRoles?.includes(4);

  const handleTableChange = (_, filters) => {
    if (filters.moneyCollectionType) {
      if (filters.moneyCollectionType.length > 1) {
        updateSearchValues("moneyCollectionType", "");
      } else {
        updateSearchValues(
          "moneyCollectionType",
          filters.moneyCollectionType[0],
        );
      }
    } else if (searchValues.moneyCollectionType) {
      updateSearchValues("moneyCollectionType", "");
    }
  };

  const updateSearchValues = (property, newValue, otherProperty) => {
    const copyOfSearchObj = { ...searchValues };
    copyOfSearchObj[property] = newValue;
    if (otherProperty) {
      copyOfSearchObj[otherProperty.property] = otherProperty.value;
    }
    setSearchValues(copyOfSearchObj);
  };

  const handleDateChange = (dates, dataIndex) => {
    if (dataIndex === "offerDate") {
      setCreationDateRange(dates);
    } else {
      setSellingDateRange(dates);
    }
    if (dates) {
      const startDate = dates[0].format("YYYY-MM-DD");
      const endDate = dates[1].format("YYYY-MM-DD");
      if (dataIndex === "offerDate") {
        updateSearchValues("from", startDate, {
          property: "to",
          value: endDate,
        });
      } else {
        updateSearchValues("sellingDateFrom", startDate, {
          property: "sellingDateTo",
          value: endDate,
        });
      }
    } else {
      if (dataIndex === "offerDate") {
        updateSearchValues("from", "", { property: "to", value: "" });
      } else {
        updateSearchValues("sellingDateFrom", "", {
          property: "sellingDateTo",
          value: "",
        });
      }
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setLoading(true);
    }
    setCurrentPage(page);
  };

  const handlePageSizeChange = (current, newSize) => {
    setLoading(true);
    setPageSize(newSize);
  };

  const handleExportDataToExcel = () => {
    if (isExporting) return;
    setIsExporting(true);
    const baseProps = [
      { title: "Name", prop: "fullName" },
      { title: "Mobile", prop: "phone" },
      { title: "Type", prop: "categoryName" },
      { title: "Sealed Quantity", prop: "sealedQuantity" },
      { title: "Offer Price", prop: "offeredPricePerKilo" },
      { title: "Commission", prop: "commission" },
      {
        title: "Total Price (Before Commission)",
        prop: "totalOfferPriceBeforeCommission",
      },
      {
        title: "Total Price (After Commission)",
        prop: "totalOfferPriceAfterCommission",
      },
      { title: "Collection Type", prop: "moneyCollectionType" },
      { title: "Cash From User", prop: "cashFromUser" },
      { title: "ID Number", prop: "idNumber" },
      { title: "Cash To User", prop: "cashToUser" },
      { title: "Selling Date", prop: "sellingDate" },
      { title: "Creation Date", prop: "offerDate" },
      { title: "Offered By", prop: "offerdBy" },
      { title: "Sales Person", prop: "salesPerson" },
      { title: "Management Fees", prop: "managementFees" },
    ];
    const payload = {
      isExport: true,
      currentPage,
      perPage: pageSize,
      ...searchValues,
    };
    for (let property in payload) {
      if (
        !payload[property] &&
        payload[property] !== 0 &&
        payload[property] !== false
      ) {
        delete payload[property];
      }
    }
    getBuyersTransferedToBuyAndSupplyWalletApi(
      payload,
      (res) => {
        if (res.success) {
          exportDataAsExcel(res.data.response, {
            baseProps,
            tab: "Extend to Buy and Supply Wallet Report",
          });
        } else {
          toastError(res.message);
          exportDataAsExcel(res.data.response, {
            baseProps,
            tab: "Extend to Buy and Supply Wallet Report",
          });
          setIsExporting(false);
        }
        setIsExporting(false);
      },
      (res) => {
        setIsExporting(false);
        toastError(res?.data?.error);
      },
    );
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    setLoading(true);
    switch (dataIndex) {
      case "fullName":
        updateSearchValues("userName", selectedKeys[0]);
        break;
      case "phone":
        updateSearchValues("userMobile", selectedKeys[0]);
        break;
      case "categoryName":
        updateSearchValues("type", selectedKeys[0]);
        break;
      case "sealedQuantity":
        updateSearchValues("quantity", selectedKeys[0]);
        break;
      case "offeredPricePerKilo":
        updateSearchValues("price", selectedKeys[0]);
        break;
      case "commission":
        updateSearchValues("commission", selectedKeys[0]);
        break;
      case "totalOfferPriceBeforeCommission":
        updateSearchValues("totalPriceBeforeCommission", selectedKeys[0]);
        break;
      case "totalOfferPriceAfterCommission":
        updateSearchValues("totalPriceAfterCommission", selectedKeys[0]);
        break;
      case "cashFromUser":
        updateSearchValues("cashFromUser", selectedKeys[0]);
        break;
      case "cashToUser":
        updateSearchValues("cashToUser", selectedKeys[0]);
        break;
      case "moneyCollectionType":
        updateSearchValues("moneyCollectionType", selectedKeys[0]);
        break;
      case "offeredBy":
        updateSearchValues("offeredBy", selectedKeys[0]);
        break;
      case "salesPerson":
        updateSearchValues("salesPerson", selectedKeys[0]);
        break;
      default:
        break;
    }
    confirm();
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    setLoading(true);
    switch (dataIndex) {
      case "fullName":
        updateSearchValues("userName", "");
        break;
      case "phone":
        updateSearchValues("userMobile", "");
        break;
      case "categoryName":
        updateSearchValues("type", "");
        break;
      case "sealedQuantity":
        updateSearchValues("quantity", "");
        break;
      case "offeredPricePerKilo":
        updateSearchValues("price", "");
        break;
      case "commission":
        updateSearchValues("commission", "");
        break;
      case "totalOfferPriceBeforeCommission":
        updateSearchValues("totalPriceBeforeCommission", "");
        break;
      case "totalOfferPriceAfterCommission":
        updateSearchValues("totalPriceAfterCommission", "");
        break;
      case "cashFromUser":
        updateSearchValues("cashFromUser", "");
        break;
      case "cashToUser":
        updateSearchValues("cashToUser", "");
        break;
      case "moneyCollectionType":
        updateSearchValues("moneyCollectionType", "");
        break;
      case "offeredBy":
        updateSearchValues("offeredBy", "");
        break;
      case "salesPerson":
        updateSearchValues("salesPerson", "");
        break;
      default:
        break;
    }
    clearFilters();
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onKeyDown={(e) => {
            const acceptsNumbersOnly = [
              "phone",
              "sealedQuantity",
              "commission",
              "totalOfferPriceBeforeCommission",
              "totalOfferPriceAfterCommission",
              "offeredPricePerKilo",
              "cashFromUser",
              "cashToUser",
            ];
            if (acceptsNumbersOnly.includes(dataIndex)) {
              handleAllowNumbersOnly(e, true);
            }
          }}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleReset(clearFilters, dataIndex, confirm)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1890ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) => true,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const getColumnDateProps = (dataIndex) => {
    const dateValue =
      dataIndex === "offerDate" ? creationDateRange : sellingDateRange;
    return {
      filterDropdown: () => (
        <div
          style={{
            padding: 8,
          }}
          className="range-picker-holder"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <RangePicker
            value={dateValue}
            allowClear
            popupClassName="main-date-picker"
            inputReadOnly={false}
            onChange={(dates) => {
              setCurrentPage(1);
              handleDateChange(dates, dataIndex);
            }}
            onKeyDown={(e) => e.preventDefault()}
          />
        </div>
      ),
      filterIcon: () => (
        <FilterOutlined
          style={dateValue?.length > 0 ? { color: "blue" } : {}}
        />
      ),
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
      render: (text) => text,
    };
  };

  const columns = [
    {
      key: "fullName",
      title: "NAME",
      dataIndex: "fullName",
      ...getColumnSearchProps("fullName"),
    },
    {
      key: "phone",
      title: "MOBILE",
      dataIndex: "phone",
      ...getColumnSearchProps("phone"),
    },
    { key: "idNumber", title: "ID Number", dataIndex: "idNumber" },
    {
      key: "categoryName",
      title: "TYPE",
      dataIndex: "categoryName",
      ...getColumnSearchProps("categoryName"),
    },
    {
      key: "sealedQuantity",
      title: "SEALED QUANTITY",
      dataIndex: "sealedQuantity",
      ...getColumnSearchProps("sealedQuantity"),
    },
    {
      key: "offeredPricePerKilo",
      title: "OFFER PRICE",
      dataIndex: "offeredPricePerKilo",
      ...getColumnSearchProps("offeredPricePerKilo"),
    },
    {
      key: "commission",
      title: "COMMISSION",
      dataIndex: "commission",
      ...getColumnSearchProps("commission"),
    },
    {
      key: "totalOfferPriceAfterCommission",
      title: "TOTAL PRICE (AFTER COMMISSION)",
      dataIndex: "totalOfferPriceAfterCommission",
      ...getColumnSearchProps("totalOfferPriceAfterCommission"),
    },
    {
      key: "totalOfferPriceBeforeCommission",
      title: "TOTAL PRICE (BEFORE COMMISSION)",
      dataIndex: "totalOfferPriceBeforeCommission",
      ...getColumnSearchProps("totalOfferPriceBeforeCommission"),
    },
    {
      key: "moneyCollectionType",
      title: "COLLECTION TYPE",
      dataIndex: "moneyCollectionType",
      filters: [
        {
          text: "Cash",
          value: 1,
        },
        {
          text: "Store",
          value: 2,
        },
      ],
    },
    {
      key: "cashFromUser",
      title: "CASH FROM USER",
      dataIndex: "cashFromUser",
      ...getColumnSearchProps("cashFromUser"),
    },
    {
      key: "cashToUser",
      title: "CASH TO USER",
      dataIndex: "cashToUser",
      ...getColumnSearchProps("cashToUser"),
    },
    {
      key: "sellingDate",
      title: "SELLING DATE",
      dataIndex: "sellingDate",
      ...getColumnDateProps("sellingDate"),
    },
    {
      key: "offerDate",
      title: "CREATION DATE",
      dataIndex: "offerDate",
      ...getColumnDateProps("offerDate"),
    },
    {
      key: "offeredBy",
      title: "OFFERED BY",
      dataIndex: "offeredBy",
      ...getColumnSearchProps("offeredBy"),
    },
    {
      key: "salesPerson",
      title: "SALES PERSON",
      dataIndex: "salesPerson",
      ...getColumnSearchProps("salesPerson"),
    },
    {
      key: "managementFees",
      title: "MANAGEMENT FEES",
      dataIndex: "managementFees",
      render: (managementFees) =>
        managementFees ? `${managementFees} EGP` : "",
    },
    {
      key: "beforeStorageDuration",
      title: "BEFORE STORAGE DURATION",
      dataIndex: "beforeStorageDuration",
      render: (beforeStorageDuration) => {
        return (
          <span>
            {beforeStorageDuration ? (
              <span style={{ color: "green" }}>True</span>
            ) : (
              <span style={{ color: "red" }}>False</span>
            )}
          </span>
        );
      },
    },
  ];

  const isSalesColumns = [
    {
      key: "fullName",
      title: "NAME",
      dataIndex: "fullName",
      ...getColumnSearchProps("fullName"),
    },
    {
      key: "phone",
      title: "MOBILE",
      dataIndex: "phone",
      ...getColumnSearchProps("phone"),
    },
    {
      key: "categoryName",
      title: "TYPE",
      dataIndex: "categoryName",
      ...getColumnSearchProps("categoryName"),
    },
    {
      key: "sealedQuantity",
      title: "SEALED QUANTITY",
      dataIndex: "sealedQuantity",
      ...getColumnSearchProps("sealedQuantity"),
    },
    {
      key: "offeredPricePerKilo",
      title: "OFFER PRICE",
      dataIndex: "offeredPricePerKilo",
      ...getColumnSearchProps("offeredPricePerKilo"),
    },
    {
      key: "totalOfferPriceAfterCommission",
      title: "TOTAL PRICE (AFTER COMMISSION)",
      dataIndex: "totalOfferPriceAfterCommission",
      ...getColumnSearchProps("totalOfferPriceAfterCommission"),
    },
    {
      key: "moneyCollectionType",
      title: "COLLECTION TYPE",
      dataIndex: "moneyCollectionType",
      filters: [
        {
          text: "Cash",
          value: 1,
        },
        {
          text: "Store",
          value: 2,
        },
      ],
    },
    {
      key: "cashFromUser",
      title: "CASH FROM USER",
      dataIndex: "cashFromUser",
      ...getColumnSearchProps("cashFromUser"),
    },
    {
      key: "cashToUser",
      title: "CASH TO USER",
      dataIndex: "cashToUser",
      ...getColumnSearchProps("cashToUser"),
    },
    {
      key: "sellingDate",
      title: "SELLING DATE",
      dataIndex: "sellingDate",
      ...getColumnDateProps("sellingDate"),
    },
    {
      key: "offerDate",
      title: "CREATION DATE",
      dataIndex: "offerDate",
      ...getColumnDateProps("offerDate"),
    },
  ];

  useEffect(() => {
    const payload = {
      pageNumber: currentPage,
      perPage: pageSize,
      ...searchValues,
    };
    for (let property in payload) {
      if (!payload[property] && payload[property] !== 0) {
        delete payload[property];
      }
    }
    getBuyersTransferedToBuyAndSupplyWalletApi(
      payload,
      (res) => {
        if (res.success) {
          setLoading(false);
          setDataSource(res.data.response);
          setTotalCount(res.data.totalCount);
        } else {
          toastError(res.message);
        }
      },
      (res) => {
        setLoading(false);
        toastError(res?.data?.error);
      },
    );
  }, [searchValues, pageSize, currentPage]);

  return (
    <section className="users pending-offers">
      <div className="users__all-contents ">
        <div className="users__content">
          <div className="users__usersData">
            <Header title="Extend to Buy and Supply Wallet" />
            <SubHeader
              bgColor="#356ab3"
              title="All Extended to Buy and Supply Wallet"
            />
            <div className="users__table grid-table mrg-ye-50 ">
              <GridTable
                loading={loading}
                columns={isSales ? isSalesColumns : columns}
                dataSource={dataSource}
                rowId={"purchaserequestId"}
                allowExport={true}
                apiExport={true}
                isExporting={isExporting}
                onExport={handleExportDataToExcel}
                title={"Extend to Buy and Supply Wallet"}
                currentPage={currentPage}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                pageSize={pageSize}
                onChange={handleTableChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ExtendToBuyAndSupplyWallet;
